import { useState, useEffect, useReducer, useRef, useMemo } from 'react';
import clsx from 'clsx';
import { Link } from '@tanstack/react-router';
import { Panel, Skeleton, EmptyState } from '@/components/ui';
import { StatusDropdown } from '@/components/pipeline';
import { PIPELINE_STATUS_COLORS } from '@/types';
import { useConversations } from '@/hooks/use-conversations-v2';
import { conversationToDeal } from '@/types';
import {
  type PipelineStatus,
  PIPELINE_STATUS_ORDER,
} from '@arcanum-hq/shared-types';
import { capitalWeightedIrr } from '@/lib/pipeline-irr';

// conversation-model-versioning Phase 5 Session 5 (ADR 0018):
// canonical 7 columns per spec §7 verbatim order.
// Dropped: 'Market' (was col 2), 'Target IRR', 'YoC'.
// Added: 'IRR' (col 4), 'DSCR' (col 5). Market moved to col 6.
const COLUMNS = [
  'Deal Name',
  'Units',
  'Basis',
  'IRR',
  'DSCR',
  'Market',
  'Status',
];

// 6 filterable statuses: the 5 in PIPELINE_STATUS_ORDER + 'Passed' (side action)
// Session 9: rendered as multi-select chips in the cockpit panel header.
const ALL_FILTER_STATUSES: PipelineStatus[] = [
  ...PIPELINE_STATUS_ORDER,
  'Passed',
];

/** Coerce a string|null|undefined metric to display string.
 *  null / undefined / empty → '—'.
 */
function coerceMetric(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '—';
  return raw;
}

// ── StatusFilterChip (cockpit-local) ─────────────────────────────────────────
// Inline filter chip — uses S7 `status-*` tokens.
// Static literal maps required — Tailwind v4 only extracts complete literal
// class strings; interpolated fragments (`text-${token}`) are silently absent
// from the compiled stylesheet (F03 — CLAUDE.md dynamic-Tailwind footgun).
// Component-local; NOT a new primitive (single use site per CLAUDE.md rule).

/** Selected-state class strings — all 6 PipelineStatus keys, all literals. */
const CHIP_SELECTED: Record<PipelineStatus, string> = {
  Analyzing:
    'text-status-analyzing border-status-analyzing/40 bg-status-analyzing/10',
  'In Market':
    'text-status-in-market border-status-in-market/40 bg-status-in-market/10',
  'LOI Submitted':
    'text-status-loi-submitted border-status-loi-submitted/40 bg-status-loi-submitted/10',
  'Under Contract':
    'text-status-under-contract border-status-under-contract/40 bg-status-under-contract/10',
  Closed: 'text-status-closed border-status-closed/40 bg-status-closed/10',
  Passed: 'text-status-passed border-status-passed/40 bg-status-passed/10',
};

/** Dot color class — solid fill, all 6 PipelineStatus keys, all literals. */
const CHIP_DOT: Record<PipelineStatus, string> = {
  Analyzing: 'bg-status-analyzing',
  'In Market': 'bg-status-in-market',
  'LOI Submitted': 'bg-status-loi-submitted',
  'Under Contract': 'bg-status-under-contract',
  Closed: 'bg-status-closed',
  Passed: 'bg-status-passed',
};

interface StatusFilterChipProps {
  status: PipelineStatus;
  selected: boolean;
  onToggle: (s: PipelineStatus) => void;
}

function StatusFilterChip({
  status,
  selected,
  onToggle,
}: StatusFilterChipProps) {
  return (
    <button
      type="button"
      onClick={() => onToggle(status)}
      className={clsx(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[2px] border',
        'font-mono text-[8px] uppercase tracking-[0.4px] font-semibold',
        'transition-colors cursor-pointer',
        selected
          ? CHIP_SELECTED[status]
          : [
              'text-text-dim',
              'border-panel-border',
              'bg-panel-bg',
              'hover:text-text-muted',
            ]
      )}
      aria-pressed={selected}
      data-status={status}
    >
      {selected && (
        <span
          className={clsx('w-1 h-1 rounded-full shrink-0', CHIP_DOT[status])}
          aria-hidden="true"
        />
      )}
      {status}
    </button>
  );
}

// ── PipelineSummary ───────────────────────────────────────────────────────────
// Session 9: component-local multi-select filter (cockpit is not a route —
// no validateSearch; state is ephemeral, not persisted, not user_id-scoped).
export function PipelineSummary() {
  const { data: conversationsData, isLoading, error } = useConversations();

  // ADR 0018 client-side gate: only show conversations WITH ≥1 version.
  const allDeals = useMemo(
    () =>
      (conversationsData?.data ?? [])
        .filter((c) => c.hasVersion === true)
        .map(conversationToDeal),
    [conversationsData]
  );

  // Component-local multi-select filter state (ephemeral — not persisted).
  // Empty = show all (spec §8).
  const [selectedStatuses, setSelectedStatuses] = useState<PipelineStatus[]>(
    []
  );

  const handleToggleStatus = (status: PipelineStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  // ONE filtered array feeds BOTH the row list AND the weighted-IRR card.
  const filteredDeals = useMemo(() => {
    if (selectedStatuses.length === 0) return allDeals;
    return allDeals.filter((d) => selectedStatuses.includes(d.status));
  }, [allDeals, selectedStatuses]);

  // Capital-weighted Pipeline IRR over the FILTERED subset (spec §8).
  // ADR 0018 BLOCKING invariant: weight + displayed IRR from the same
  // displayVersion snapshot per row (conversationToDeal already resolves both).
  const weightedIrr = useMemo(
    () =>
      capitalWeightedIrr(
        filteredDeals.map((d) => ({
          irr: d.irr ?? null,
          basis: d.basis ?? null,
        }))
      ),
    [filteredDeals]
  );

  // Bidirectional hover subscriber — listens for `source: 'pin'` events from
  // <PipelineMap>; ignores own `source: 'row'` emissions (self-source guard).
  // Updates a ref + tick-only useReducer to re-render without state churn.
  // Strict-mode safe: useEffect cleanup removes the listener; no setTimeout,
  // no `lastConsumedRef`-style dedupe (per research § 4.13 verbatim).
  // PRESERVED VERBATIM — cockpit-lovable-alignment Phase 3 contract (risk #45).
  const hoveredFromPinRef = useRef<string | null>(null);
  const [, forceTick] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (e: Event): void => {
      const detail = (
        e as CustomEvent<{ dealId: string | null; source: 'row' | 'pin' }>
      ).detail;
      if (!detail) return;
      if (detail.source === 'row') return; // self-source guard
      if (hoveredFromPinRef.current === detail.dealId) return; // no-op
      hoveredFromPinRef.current = detail.dealId;
      forceTick();
    };
    window.addEventListener('arcanum:pipeline-row-highlight', handler);
    return () =>
      window.removeEventListener('arcanum:pipeline-row-highlight', handler);
  }, []);

  // Loading
  if (isLoading) {
    return (
      <div style={{ gridColumn: '1 / 3' }}>
        <Panel title="Your Pipeline" className="h-full">
          <div className="flex flex-col gap-3 p-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </Panel>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div style={{ gridColumn: '1 / 3' }}>
        <Panel title="Your Pipeline" className="h-full">
          <EmptyState
            title="FAILED TO LOAD"
            description={error.message}
            action={
              <button
                onClick={() => window.location.reload()}
                className="px-3 py-1 bg-accent-dim border border-accent/15 rounded-[2px] text-accent text-sm font-mono font-semibold cursor-pointer"
              >
                Retry
              </button>
            }
          />
        </Panel>
      </div>
    );
  }

  // Empty — re-introduces the + Add Deal CTA (supersedes ADR 0021's no-CTA
  // decision per user direction 2026-05-25). The Pipeline Map panel renders
  // independently in the cockpit grid so the empty US-path is still visible
  // regardless of this panel's state.
  if (allDeals.length === 0) {
    return (
      <div style={{ gridColumn: '1 / 3' }}>
        <Panel title="Your Pipeline" className="h-full">
          <EmptyState
            title="NO DEALS YET"
            description="Your pipeline is empty. Start a new deal in the Builder."
            action={
              <Link
                to="/builder"
                search={{ tab: 'workbench' as const }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-dim border border-accent/15 rounded-[2px] text-accent text-[11px] font-mono font-semibold tracking-[0.4px] uppercase cursor-pointer hover:bg-accent/10 transition-colors"
                data-testid="cockpit-pipeline-empty-add-deal"
              >
                + Add Deal
              </Link>
            }
          />
        </Panel>
      </div>
    );
  }

  // Success
  return (
    <div style={{ gridColumn: '1 / 3' }}>
      <Panel
        title="Your Pipeline"
        className="h-full"
        right={
          /* Filter chips + weighted IRR in the panel header right slot */
          <div className="flex items-center gap-2">
            {/* Weighted IRR badge */}
            <div className="flex items-center gap-1.5 border border-panel-border rounded-[2px] px-2 py-0.5 bg-void">
              <span className="text-[8px] font-mono uppercase tracking-[0.5px] text-text-dim">
                Wtd. IRR
              </span>
              <span className="text-[11px] font-mono font-semibold tabular-nums text-accent">
                {weightedIrr}
              </span>
            </div>
          </div>
        }
      >
        {/* Filter chip row */}
        <div className="flex items-center gap-1 px-2 pt-1.5 pb-1 border-b border-panel-border flex-wrap">
          {ALL_FILTER_STATUSES.map((s) => (
            <StatusFilterChip
              key={s}
              status={s}
              selected={selectedStatuses.includes(s)}
              onToggle={handleToggleStatus}
            />
          ))}
          {selectedStatuses.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedStatuses([])}
              className="text-[8px] font-mono text-text-dim hover:text-text-muted cursor-pointer px-1 underline underline-offset-2"
            >
              clear
            </button>
          )}
        </div>

        {/* Empty-filtered state — ADR 0021/S5: no action slot */}
        {filteredDeals.length === 0 ? (
          <EmptyState
            title="NO DEALS MATCH FILTER"
            description="No deals have this status."
          />
        ) : (
          <div className="flex-1 overflow-y-auto min-h-0">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {COLUMNS.map((h) => (
                    <th
                      key={h}
                      className="text-left px-2 py-1 text-[8px] text-text-dim font-mono font-medium border-b border-panel-border uppercase tracking-[0.6px]"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDeals.map((d) => {
                  const sc =
                    PIPELINE_STATUS_COLORS[d.status] || 'var(--color-text-dim)';
                  const isHoveredFromPin = hoveredFromPinRef.current === d.id;
                  return (
                    <tr
                      key={d.id}
                      className="deal-row"
                      onMouseEnter={() =>
                        window.dispatchEvent(
                          new CustomEvent('arcanum:pipeline-row-highlight', {
                            detail: { dealId: d.id, source: 'row' },
                          })
                        )
                      }
                      onMouseLeave={() =>
                        window.dispatchEvent(
                          new CustomEvent('arcanum:pipeline-row-highlight', {
                            detail: { dealId: null, source: 'row' },
                          })
                        )
                      }
                      style={
                        isHoveredFromPin
                          ? { background: 'rgba(20, 28, 40, 0.4)' }
                          : undefined
                      }
                    >
                      {/* Deal Name — col 1 */}
                      <td
                        className="px-2 py-2 text-[11px] font-mono text-text-bright font-medium"
                        style={{
                          borderLeft: `2px solid ${sc}`,
                          paddingLeft: '12px',
                        }}
                      >
                        {d.name}
                      </td>
                      {/* Units — col 2, tabular-nums */}
                      <td className="px-2 py-2 text-[11px] font-mono text-text-bright tabular-nums">
                        {d.units != null ? String(d.units) : '—'}
                      </td>
                      {/* Basis — col 3, tabular-nums */}
                      <td className="px-2 py-2 text-[11px] font-mono text-text-bright tabular-nums">
                        {coerceMetric(d.basis)}
                      </td>
                      {/* IRR — col 4, tabular-nums */}
                      <td className="px-2 py-2 text-[11px] font-mono text-text-bright tabular-nums">
                        {coerceMetric(d.irr)}
                      </td>
                      {/* DSCR — col 5, tabular-nums */}
                      <td className="px-2 py-2 text-[11px] font-mono text-text-bright tabular-nums">
                        {coerceMetric(d.dscr)}
                      </td>
                      {/* Market — col 6 */}
                      <td className="px-2 py-2 text-[11px] font-mono text-text-bright">
                        {coerceMetric(d.market)}
                      </td>
                      {/* Status — col 7 */}
                      <td className="px-2 py-2">
                        <StatusDropdown
                          conversationId={d.id}
                          status={d.status}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
